// src/pages/Appli/RDVAgenda/DuplicateWeekRDVModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../components/Button/Button";
import { FaClone, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { addRDV } from "../../../services/rdvAgendaServices";

/* Helpers */
const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const parseISODate = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return null;
  return new Date(`${yyyy_mm_dd}T00:00:00`);
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const startOfMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfSunday = (date) => {
  const monday = startOfMonday(date);
  const sunday = addDays(monday, 6);
  sunday.setHours(23, 59, 59, 999);
  return sunday;
};

const hhmm = (t) => (t ? String(t).slice(0, 5) : "");

const toHHMMSS = (t) => {
  if (!t) return "";
  const s = String(t);
  if (s.length === 5) return `${s}:00`;
  return s;
};

const isReservedRDV = (rdv) =>
  !!(rdv?.nom_client && String(rdv.nom_client).trim().length > 0);

const DuplicateWeekRDVModal = ({ isOpen, onClose, rdvs = [], onDone, defaultSourceDate }) => {
  const [sourceDate, setSourceDate] = useState(defaultSourceDate || toISODate(new Date()));
  const [targetDate, setTargetDate] = useState(toISODate(addDays(new Date(), 7)));

  const [includeReserved, setIncludeReserved] = useState(false);
  const [asAvailable, setAsAvailable] = useState(true);
  const [copyLieuNote, setCopyLieuNote] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSourceDate(defaultSourceDate || toISODate(new Date()));
    setTargetDate(toISODate(addDays(new Date(), 7)));
    setIncludeReserved(false);
    setAsAvailable(true);
    setCopyLieuNote(true);
    setLoading(false);
  }, [isOpen, defaultSourceDate]);

  const sourceRange = useMemo(() => {
    const src = parseISODate(sourceDate) || new Date();
    const start = startOfMonday(src);
    const end = endOfSunday(src);
    return { start, end };
  }, [sourceDate]);

  const targetRange = useMemo(() => {
    const tgt = parseISODate(targetDate) || new Date();
    const start = startOfMonday(tgt);
    const end = endOfSunday(tgt);
    return { start, end };
  }, [targetDate]);

  const sourceWeekRDVs = useMemo(() => {
    return (rdvs || [])
      .filter((r) => {
        const d = parseISODate(r.date_rdv);
        if (!d) return false;
        return d >= sourceRange.start && d <= sourceRange.end;
      })
      .filter((r) => (includeReserved ? true : !isReservedRDV(r)))
      .sort((a, b) => {
        const sa = `${a.date_rdv || ""} ${hhmm(a.heure_debut) || ""}`;
        const sb = `${b.date_rdv || ""} ${hhmm(b.heure_debut) || ""}`;
        return sa.localeCompare(sb, "fr");
      });
  }, [rdvs, sourceRange, includeReserved]);

  const deltaDays = useMemo(() => {
    // décalage = (target monday) - (source monday) en jours
    const ms = targetRange.start.getTime() - sourceRange.start.getTime();
    return Math.round(ms / (1000 * 60 * 60 * 24));
  }, [sourceRange, targetRange]);

  const handleDuplicate = async () => {
    if (!sourceWeekRDVs.length) {
      toast.info("Aucun RDV à dupliquer sur la semaine source.");
      return;
    }

    if (
      !window.confirm(
        `Dupliquer ${sourceWeekRDVs.length} RDV(s) vers la semaine du ${toISODate(
          targetRange.start
        )} ?`
      )
    ) {
      return;
    }

    setLoading(true);

    try {
      const results = await Promise.allSettled(
        sourceWeekRDVs.map((r) => {
          const d = parseISODate(r.date_rdv) || new Date();
          const newDate = toISODate(addDays(d, deltaDays));

          const payload = {
            date_rdv: newDate,
            heure_debut: toHHMMSS(hhmm(r.heure_debut)),
            heure_fin: toHHMMSS(hhmm(r.heure_fin)),
            lieu: copyLieuNote ? r.lieu || "" : "",
            note_client: copyLieuNote ? r.note_client || "" : "",
            // clients
            nom_client: "",
            prenom_client: "",
            tel_client: "",
          };

          // si on ne force pas “disponible”, on peut conserver la réservation (option)
          if (!asAvailable && includeReserved && isReservedRDV(r)) {
            payload.nom_client = r.nom_client || "";
            payload.prenom_client = r.prenom_client || "";
            payload.tel_client = r.tel_client || "";
          }

          return addRDV(payload);
        })
      );

      const ok = results.filter((x) => x.status === "fulfilled").length;
      const ko = results.length - ok;

      if (ok) toast.success(`Duplication OK : ${ok} RDV(s) créé(s) ✅`);
      if (ko) toast.error(`Duplication partielle : ${ko} échec(s) ❌`);

      await onDone?.();
    } catch (error) {
      console.error("Erreur duplication semaine :", error);
      toast.error("Erreur lors de la duplication de semaine.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl border border-white/10">
        <div className="bg-primary text-white p-4 rounded-t-2xl flex items-center justify-between">
          <h3 className="text-lg font-extrabold uppercase">
            Dupliquer une semaine de RDV
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-xl bg-white/15 hover:bg-white/20"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Semaine source (choisir une date dans la semaine)
              </label>
              <input
                type="date"
                value={sourceDate}
                onChange={(e) => setSourceDate(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              />
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                Semaine : {toISODate(sourceRange.start)} → {toISODate(sourceRange.end)}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Semaine cible (choisir une date dans la semaine)
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              />
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                Semaine : {toISODate(targetRange.start)} → {toISODate(targetRange.end)}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/40">
            <div className="font-bold text-gray-800 dark:text-gray-100 mb-2">
              Options
            </div>

            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={includeReserved}
                  onChange={(e) => setIncludeReserved(e.target.checked)}
                />
                Inclure aussi les RDV déjà réservés (sinon seulement les créneaux disponibles)
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={asAvailable}
                  onChange={(e) => setAsAvailable(e.target.checked)}
                />
                Dupliquer en tant que créneaux disponibles (recommandé)
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={copyLieuNote}
                  onChange={(e) => setCopyLieuNote(e.target.checked)}
                />
                Copier lieu + note
              </label>

              {!asAvailable && includeReserved && (
                <div className="text-xs text-orange-700 dark:text-orange-300">
                  ⚠️ Tu as choisi de conserver des infos client sur des RDV réservés. Vérifie que c’est bien voulu.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="font-bold text-gray-800 dark:text-gray-100 mb-2">
              RDV à dupliquer (semaine source)
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-200">
              {sourceWeekRDVs.length} RDV(s)
            </div>

            <div className="mt-3 max-h-56 overflow-y-auto space-y-2">
              {sourceWeekRDVs.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-800/50"
                >
                  <div className="text-sm">
                    <div className="font-semibold">
                      {r.date_rdv} — {hhmm(r.heure_debut)}→{hhmm(r.heure_fin)}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      {isReservedRDV(r) ? "Réservé" : "Disponible"} {r.lieu ? `· ${r.lieu}` : ""}
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    +{deltaDays} jour(s)
                  </div>
                </div>
              ))}
              {!sourceWeekRDVs.length && (
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  Aucun RDV à dupliquer avec ces options.
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="btn-ghost gap-2"
              disabled={loading}
            />
            <Button
              text={loading ? "Duplication..." : "Dupliquer"}
              icon={FaClone}
              onClick={handleDuplicate}
              type="button"
              className="btn-primary gap-2"
              disabled={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateWeekRDVModal;
